import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store/index";
import avatar from "assets/icons/avatar.png";
import arrowIcon from "assets/icons/sidebar/arrow-down.svg";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { clearGeoFilter, setGeoFilter } from "store/geoFilter/slice";
import { useGuideModal } from "modals/guides/useGuideModal";
import { GuideButton } from "modals/guides/GuideButton";
import { getBaseCurrency } from "store/payments/currencies/slice";
import Tooltip from "components/Tooltip";
import { useTranslation } from "react-i18next";
import SettingsModal from "./SettingsModal";
import ProfileModal from "./ProfileModal";
import styles from "./AccountMenu.module.scss";

const ALL_GEO_VALUE = "__all_geo__";

const Header: React.FC = () => {
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isWidgetsPage = location.pathname.startsWith("/cms/widgets");
  const pathnames = location.pathname.split("/").filter(Boolean);
  const breadcrumbLabels: Record<string, string> = {
    kyc: "KYC",
  };
  const formatLabel = (segment: string) => {
    return segment
      .split("-")
      .map((word) => {
        const lowerWord = word.toLowerCase();

        return (
          breadcrumbLabels[lowerWord] ||
          lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1)
        );
      })
      .join(" ");
  };

  const geoList = useAppSelector((store) => store.settings.geoList);
  const selectedGeo = useAppSelector((store) => store.geoFilter.selectedGeo);
  const baseCurrency = useAppSelector((store) => store.currencies.baseCurrency);
  const dispatch = useAppDispatch();
  const { openGuide } = useGuideModal();
  const { t } = useTranslation();

 useEffect(() => {
   dispatch(getBaseCurrency());
 }, [dispatch]);

  useEffect(() => {
    if (!isAccountMenuOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target as Node)
      ) {
        setIsAccountMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isAccountMenuOpen]);

  useEffect(() => {
    if (selectedGeo.length > 1) {
      dispatch(setGeoFilter([selectedGeo[0]]));
    }
  }, [dispatch, selectedGeo]);

  const geoItems = [
    {
      value: ALL_GEO_VALUE,
      label: `${t("all")} GEO`,
    },
    ...(geoList ?? []).map((geo) => ({
      value: geo,
      label: geo,
      icon:
        geo.toLowerCase() === "ww" ? (
          <span className="geo-dropdown__world-icon">🌎</span>
        ) : undefined,
      img:
        geo.toLowerCase() === "ww"
          ? undefined
          : `https://flagcdn.com/w40/${geo.toLowerCase()}.png`,
    })),
  ];

  const handleGeoSelect = (value: string | number) => {
    if (value === ALL_GEO_VALUE) {
      dispatch(clearGeoFilter());
      return;
    }

    dispatch(setGeoFilter([String(value)]));
  };
  return (
    <div className="header-wrapper">
      <div className="header-content">
        <nav aria-label="breadcrumb" className="breadcrumbs">
          <ul>
            {pathnames.map((segment, index) => {
              const isLast = index === pathnames.length - 1;

              return (
                <li key={segment}>
                  <span className={isLast ? "current" : ""}>
                    {formatLabel(segment)}
                  </span>

                  {!isLast && <img src={arrowIcon} alt="" />}
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="header-content--right">
          {baseCurrency && (
            <Tooltip text={t("baseCurrency")} position="bottom">
              <div className="base-currency">{baseCurrency}</div>
            </Tooltip>
          )}
          {isWidgetsPage && <GuideButton onClick={() => openGuide("cms")} />}
          <DefaultDropdown
            value={selectedGeo[0]}
            placeholder="GEO"
            options={geoItems}
            onSelect={handleGeoSelect}
          />
          <div className={styles.account} ref={accountMenuRef}>
            <button
              className={`avatar ${styles.trigger}`}
              type="button"
              aria-label={t("profile")}
              aria-haspopup="menu"
              aria-expanded={isAccountMenuOpen}
              onClick={() => setIsAccountMenuOpen((isOpen) => !isOpen)}
            >
              <img src={avatar} alt="" />
            </button>

            <div
              className={`${styles.menu} ${
                isAccountMenuOpen ? styles.menuOpen : ""
              }`}
              role="menu"
              aria-hidden={!isAccountMenuOpen}
            >
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={isAccountMenuOpen ? 0 : -1}
                  onClick={() => {
                    setIsAccountMenuOpen(false);
                    setIsProfileOpen(true);
                  }}
                >
                  {t("profile")}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={isAccountMenuOpen ? 0 : -1}
                  onClick={() => {
                    setIsAccountMenuOpen(false);
                    setIsSettingsOpen(true);
                  }}
                >
                  {t("settings")}
                </button>
            </div>
          </div>
        </div>
      </div>
      {isProfileOpen && (
        <ProfileModal onClose={() => setIsProfileOpen(false)} />
      )}
      {isSettingsOpen && (
        <SettingsModal onClose={() => setIsSettingsOpen(false)} />
      )}
    </div>
  );
};

export default Header;
